import { formatExamTime, EXAM_TYPE_LABEL } from '@exam-center/exam-event-management';
import { type ExamType, TagCategory } from '@exam-center/graphql';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Text,
} from '@shared/components';
import { formatDate } from '@shared/utils';

export interface ExportConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  examStartedAt: Date;
  examEndedAt: Date;
  examRound: number;
  examCode: string;
  category: TagCategory;
  totalRecords: number;
  examType: ExamType;
  loading?: boolean;
}

const TAG_CATEGORY_LABEL: Partial<Record<TagCategory, string>> = {
  [TagCategory.Part]: 'รายหมวด',
  [TagCategory.Subject]: 'รายวิชา',
};

export default function ExportConfirmationModal(props: Readonly<ExportConfirmationModalProps>) {
  const {
    open,
    onClose,
    onConfirm,
    examStartedAt,
    examEndedAt,
    examRound,
    examCode,
    category,
    totalRecords,
    examType,
    loading = false,
  } = props;

  const formattedDate = formatDate(examStartedAt);
  const formattedTime = formatExamTime(examStartedAt, examEndedAt);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='xs'
      slotProps={{
        paper: {
          sx: { minWidth: '440px' },
        },
      }}
    >
      <DialogTitle onClose={onClose}>ยืนยันการส่งออกข้อมูล</DialogTitle>
      <Divider />
      <DialogContent>
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          })}
        >
          <Text variant='body1Semibold'>สรุปข้อมูลที่ต้องการ</Text>

          <Box
            sx={(theme) => ({
              display: 'flex',
              gap: theme.spacing(2),
            })}
          >
            <Text variant='body1Semibold' sx={{ minWidth: '120px' }}>
              วันที่สอบ:
            </Text>
            <Text>{`${formattedDate}, ${formattedTime}`}</Text>
          </Box>

          <Box
            sx={(theme) => ({
              display: 'flex',
              gap: theme.spacing(2),
            })}
          >
            <Text variant='body1Semibold' sx={{ minWidth: '120px' }}>
              รอบสอบที่:
            </Text>
            <Text>{examRound}</Text>
          </Box>

          <Box
            sx={(theme) => ({
              display: 'flex',
              gap: theme.spacing(2),
            })}
          >
            <Text variant='body1Semibold' sx={{ minWidth: '120px' }}>
              หลักสูตร:
            </Text>
            <Text>{examCode}</Text>
          </Box>

          <Box
            sx={(theme) => ({
              display: 'flex',
              gap: theme.spacing(2),
            })}
          >
            <Text variant='body1Semibold' sx={{ minWidth: '120px' }}>
              ประเภทคะแนน:
            </Text>
            <Text>{TAG_CATEGORY_LABEL[category] ?? ''}</Text>
          </Box>

          <Box
            sx={(theme) => ({
              display: 'flex',
              gap: theme.spacing(2),
            })}
          >
            <Text variant='body1Semibold' sx={{ minWidth: '120px' }}>
              Exam Type:
            </Text>
            <Text>{EXAM_TYPE_LABEL[examType]}</Text>
          </Box>

          <Box
            sx={(theme) => ({
              display: 'flex',
              gap: theme.spacing(2),
            })}
          >
            <Text variant='body1Semibold' sx={{ minWidth: '120px' }}>
              รวมทั้งหมด:
            </Text>
            <Text>{totalRecords} รายการ</Text>
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          variant='outlined'
          color='secondary'
          size='large'
          onClick={onClose}
          disabled={loading}
        >
          ยกเลิก
        </Button>
        <Button
          variant='contained'
          color='primary'
          size='large'
          onClick={onConfirm}
          loading={loading}
          sx={{ minWidth: '140px' }}
        >
          ส่งออกข้อมูล
        </Button>
      </DialogActions>
    </Dialog>
  );
}
